import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Search, Server, SlidersHorizontal } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Badge,
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  FilterBuilder,
  Pagination,
  type FilterCondition,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useOpsServices, type OpsServiceRow, type OpsServicesParams } from '@/api/operations';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import { OpsStatBand } from './components/ops-stat-band';
import { opsServiceColumns, OPS_TOGGLEABLE_COLUMN_IDS } from './components/ops-service-columns';
import { opsFilterFields } from './components/ops-filter-fields';

const PAGE_SIZE = 20;

/** Debounce a fast-changing search value to avoid spamming the backend. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Fold FilterBuilder conditions into backend query params for OpsServicesParams.
 * Conditions from the "category", "status", "provider", "org_id" fields map
 * directly to their respective query params (last wins per field).
 */
function conditionsToParams(conditions: FilterCondition[]): Partial<OpsServicesParams> {
  const out: Partial<OpsServicesParams> = {};
  for (const c of conditions) {
    if (!c.value) continue;
    switch (c.field) {
      case 'category':
        out.category = c.value as OpsServicesParams['category'];
        break;
      case 'status':
        out.status = c.value as OpsServicesParams['status'];
        break;
      case 'provider':
        out.provider = c.value;
        break;
      case 'org_id': {
        const n = Number(c.value);
        if (!Number.isNaN(n)) out.org_id = n;
        break;
      }
    }
  }
  return out;
}

/**
 * Operations page — cross-org service management list.
 * Layout: PageHeader → OpsStatBand (always-on) → Toolbar (search + Filters +
 * Display options) → DataTable (server-paginated, row click → detail route).
 */
export default function OperationsPage() {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  // Column visibility — all toggleable columns on by default.
  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(OPS_TOGGLEABLE_COLUMN_IDS.map((id) => [id, true])),
  );

  const filterParams = conditionsToParams(conditions);

  const { data, isLoading, isError, isFetching, refetch } = useOpsServices({
    page,
    per_page: PAGE_SIZE,
    q: query || undefined,
    ...filterParams,
  });

  const rows: OpsServiceRow[] = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const visibleColumns = opsServiceColumns.filter(
    (c) => !OPS_TOGGLEABLE_COLUMN_IDS.includes(String(c.id)) || visible[String(c.id)] !== false,
  );

  const appliedFieldCount = new Set(conditions.map((c) => c.field)).size;

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <PageHeader
        title="Operations"
        description="Cross-org service management — inspect, filter, and navigate to any provisioned service."
      />

      {/* Stat banner — always visible, reflects full system state (not filtered). */}
      <OpsStatBand />

      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={rawQuery}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search services…"
            startSlot={<Search size={15} />}
            style={{ maxWidth: 320 }}
          />
        }
        end={
          <>
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters{' '}
                  {appliedFieldCount ? <Badge tone="primary">{appliedFieldCount}</Badge> : null}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" style={{ padding: 0 }}>
                <FilterBuilder
                  fields={opsFilterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={applyFilters}
                  onClose={() => setFilterOpen(false)}
                />
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                  Display options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                {opsServiceColumns
                  .filter((c) => OPS_TOGGLEABLE_COLUMN_IDS.includes(String(c.id)))
                  .map((c) => {
                    const id = String(c.id);
                    return (
                      <DropdownMenuCheckboxItem
                        key={id}
                        checked={visible[id] !== false}
                        onCheckedChange={(v) =>
                          setVisible((prev) => ({ ...prev, [id]: v === true }))
                        }
                        onSelect={(e) => e.preventDefault()}
                      >
                        {typeof c.header === 'string' ? c.header : id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load services"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <TabLoading rows={8} />
      ) : (
        <DataTable
          columns={visibleColumns}
          data={rows}
          getRowId={(r) => r.id}
          onRowClick={(row) => navigate(`/operations/services/${row.id}`)}
          emptyState={
            <EmptyState
              icon={<Server size={24} />}
              title="No services found"
              description="Try adjusting your search or filters."
            />
          }
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={`${view.summary}${isFetching ? ' …' : ''}`}
              />
            );
          })()}
        />
      )}
      </div>
    </Container>
  );
}
